import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Kit, KitItem } from '@/types/kit';
import { toast } from 'sonner';
import { useAuth } from '@/components/auth/AuthProvider';
import { useTranslation } from 'react-i18next';

export function useKits() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { t } = useTranslation();

  // Fetch user's kits
  const { data: kits, isLoading } = useQuery({
    queryKey: ['user-kits'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('kits')
        .select(`
          *,
          kit_items(
            *,
            inventory_items(name, category, cost_per_unit)
          )
        `)
        .returns<(Kit & { kit_items: KitItem[] })[]>();

      if (error) {
        toast.error(t('failed_load_kits'));
        return [];
      }

      return data || [];
    }
  });

  // Create a new kit
  const createKitMutation = useMutation({
    mutationFn: async (kitData: { name: string; notes?: string; items: { inventory_item_id: string; quantity: number }[] }) => {
      if (!user) {
        toast.error(t('must_be_logged_in_create_kit'));
        throw new Error('No authenticated user');
      }

      // Start a transaction
      const { data: kitResult, error: kitError } = await supabase
        .from('kits')
        .insert({ 
          name: kitData.name, 
          notes: kitData.notes || null,
          user_id: user.id
        })
        .select()
        .single();

      if (kitError || !kitResult) {
        toast.error(t('failed_create_kit'));
        throw kitError || new Error('Failed to create kit');
      }

      // Insert kit items
      if (kitData.items.length > 0) {
        const kitItemsInsert = kitData.items.map(item => ({
          kit_id: kitResult.id,
          inventory_item_id: item.inventory_item_id,
          quantity: item.quantity
        }));

        const { error: itemsError } = await supabase
          .from('kit_items')
          .insert(kitItemsInsert);

        if (itemsError) {
          toast.error(t('failed_add_items_to_kit'));
          throw itemsError;
        }
      }

      toast.success(t('kit_created_successfully'));
      queryClient.invalidateQueries({ queryKey: ['user-kits'] });
      return kitResult;
    }
  });

  // Update an existing kit
  const updateKitMutation = useMutation({
    mutationFn: async (kitData: { 
      id: string;
      name: string; 
      notes?: string; 
      items: { inventory_item_id: string; quantity: number }[];
      removedItemIds?: string[];
    }) => {
      if (!user) {
        toast.error(t('must_be_logged_in_update_kit'));
        throw new Error('No authenticated user');
      }

      // Update kit details
      const { error: kitError } = await supabase
        .from('kits')
        .update({ 
          name: kitData.name, 
          notes: kitData.notes || null,
          updated_at: new Date().toISOString()
        })
        .eq('id', kitData.id);

      if (kitError) {
        toast.error(t('failed_update_kit'));
        throw kitError;
      }

      // Remove items that were deleted
      if (kitData.removedItemIds && kitData.removedItemIds.length > 0) {
        const { error: deleteError } = await supabase
          .from('kit_items')
          .delete()
          .in('id', kitData.removedItemIds);

        if (deleteError) {
          toast.error(t('failed_remove_items_from_kit'));
          throw deleteError;
        }
      }

      // Add new items
      if (kitData.items.length > 0) {
        const kitItemsInsert = kitData.items.map(item => ({
          kit_id: kitData.id,
          inventory_item_id: item.inventory_item_id,
          quantity: item.quantity
        }));

        const { error: itemsError } = await supabase
          .from('kit_items')
          .insert(kitItemsInsert);

        if (itemsError) {
          toast.error(t('failed_add_items_to_kit'));
          throw itemsError;
        }
      }

      toast.success(t('kit_updated_successfully'));
      queryClient.invalidateQueries({ queryKey: ['user-kits'] });
    }
  });

  // Delete a kit
  const deleteKitMutation = useMutation({
    mutationFn: async (kitId: string) => {
      // First delete the kit items
      const { error: kitItemsError } = await supabase
        .from('kit_items')
        .delete()
        .eq('kit_id', kitId);

      if (kitItemsError) {
        toast.error(t('failed_delete_kit_items'));
        throw kitItemsError;
      }

      // Then delete the kit itself
      const { error: kitError } = await supabase
        .from('kits')
        .delete()
        .eq('id', kitId);

      if (kitError) {
        toast.error(t('failed_delete_kit'));
        throw kitError;
      }

      toast.success(t('kit_deleted_successfully'));
      queryClient.invalidateQueries({ queryKey: ['user-kits'] });
    }
  });

  return {
    kits,
    isLoading,
    createKit: createKitMutation.mutate,
    updateKit: updateKitMutation.mutate,
    deleteKit: deleteKitMutation.mutate
  };
}
